/* eslint-disable no-use-before-define */
/* eslint-disable max-len */
import { getParentPath, getIndexAmongSiblings } from './path';
import { getDestinationPath, getSourcePath } from './flat-tree';

/*
  Transforms tree structure into flat list of items for rendering purposes.
  We recursively go through all the elements and its children first on each level
 */
export const flattenTree = (tree, path = []) => {
  if (!tree.items || !tree.items[tree.rootId]) return [];

  return tree.items[tree.rootId].children.reduce((accum, itemId, index) => {
    // iterating through all the children on the given level
    const item = tree.items[itemId];
    const currentPath = [...path, index];
    // we create a flattened item for the current item
    const currentItem = createFlattenedItem(item, currentPath);
    // we flatten its children
    const children = flattenChildren(tree, item, currentPath);
    // append to the accumulator
    return [...accum, currentItem, ...children];
  }, []);
};

/*
  Constructs a new FlattenedItem
 */
const createFlattenedItem = (item, currentPath) => ({
  item,
  path: currentPath,
});

/*
  Flatten the children of the given subtree
*/
const flattenChildren = (tree, item, currentPath) =>
  item.isExpanded ? flattenTree({ rootId: item.id, items: tree.items }, currentPath) : [];

/*
  Changes the tree data structure with minimal reference changes.
 */
export const mutateTree = (tree, itemId, mutation) => {
  const itemToChange = tree.items[itemId];
  if (!itemToChange) {
    // Item not found
    return tree;
  }
  // Returning a clone of the tree structure and overwriting the field coming in mutation
  return {
    // rootId should not change
    rootId: tree.rootId,
    items: {
      // copy all old items
      ...tree.items,
      // overwriting only the item being changed
      [itemId]: {
        ...itemToChange,
        ...mutation,
      },
    },
  };
};

export const closeTree = (tree) => {
  const updateObject = {};

  const newItem = tree.items.root.children.forEach((id) => {
    updateObject[id] = { ...tree.items[id], isExpanded: false };
  });

  return {
    rootId: tree.rootId,
    items: {
      ...tree.items,
      ...updateObject,
    },
  };
};

export const getItem = (tree, path) => {
  let cursor = tree.items[tree.rootId];

  for (const i of path) {
    cursor = tree.items[cursor.children[i]];
  }

  return cursor;
};

export const getParent = (tree, path) => {
  const parentPath = getParentPath(path);
  return getItem(tree, parentPath);
};

export const getTreePosition = (tree, path) => {
  const parent = getParent(tree, path);
  const index = getIndexAmongSiblings(path);
  return {
    parentId: parent.id,
    index,
  };
};

const hasLoadedChildren = (item) => !!item.hasChildren && item.children.length > 0;

const isLeafItem = (item) => !item.hasChildren;

const removeItemFromTree = (tree, position) => {
  const sourceParent = tree.items[position.parentId];
  const newSourceChildren = [...sourceParent.children];
  const itemRemoved = newSourceChildren.splice(position.index, 1)[0];
  const newTree = mutateTree(tree, position.parentId, {
    children: newSourceChildren,
    hasChildren: newSourceChildren.length > 0,
    // isExpanded: newSourceChildren.length > 0 && sourceParent.isExpanded,
  });

  return {
    tree: newTree,
    itemRemoved,
  };
};

const addItemToTree = (tree, position, item, shouldExpandOnNewChildren) => {
  const destinationParent = tree.items[position.parentId];

  const newDestinationChildren = [...destinationParent.children];
  if (typeof position.index === 'undefined') {
    if (hasLoadedChildren(destinationParent) || isLeafItem(destinationParent)) {
      newDestinationChildren.push(item);
    }
  } else {
    newDestinationChildren.splice(position.index, 0, item);
  }
  return mutateTree(tree, position.parentId, {
    children: newDestinationChildren,
    hasChildren: true,
    isExpanded: shouldExpandOnNewChildren ? newDestinationChildren.length > 0 : undefined,
  });
};

export const moveItemOnTree = (tree, from, to, shouldExpandOnNewChildren) => {
  const { tree: treeWithoutSource, itemRemoved } = removeItemFromTree(tree, from);
  return addItemToTree(treeWithoutSource, to, itemRemoved, shouldExpandOnNewChildren);
};

export const calculateFinalDropPositions = (
  tree,
  flattenedTree,
  dragState,
  limitItemsToOriginalNestedDepth,
) => {
  const { source, destination, combine, horizontalLevel } = dragState;
  const sourcePath = getSourcePath(flattenedTree, source.index);
  const sourcePosition = getTreePosition(tree, sourcePath);

  if (combine) {
    return {
      sourcePosition,
      destinationPosition: {
        parentId: combine.draggableId,
      },
      sourcePath,
      destinationPath,
    };
  }

  const destinationPath = getDestinationPath(
    flattenedTree,
    source.index,
    destination.index,
    horizontalLevel,
  );

  if (!destination) {
    return { sourcePosition, destinationPosition: undefined };
  }

  const destinationPosition = {
    ...getTreePosition(tree, destinationPath),
  };

  if (limitItemsToOriginalNestedDepth) {
    // Condition to check if source is child and will become a parent;
    if (sourcePath.length === 2 && sourcePath.length > destinationPath.length) {
      return {
        sourcePosition,
        destinationPosition: {
          parentId: getItem(tree, [destinationPath[0] - 1]).id,
          index: 0,
        },
        sourcePath,
      };
    }

    // Condition to check if source is a top level parent and is trying to become a child;
    if (sourcePath.length === 1 && destinationPath.length === 2) {
      return {
        sourcePosition,
        destinationPosition: {
          parentId: 'root',
          index: source.index > destination.index ? destinationPath[0] + 1 : destinationPath[0],
        },
        sourcePath,
      };
    }
  }

  return {
    sourcePosition,
    destinationPosition,
    sourcePath,
    destinationPath,
  };
};
